"use client"

import { Badge, LogoutButton } from "@/components/atoms"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { useChat } from "@/context/ChatContext"
import { ProfileIcon } from "@/icons"
import { HttpTypes } from "@medusajs/types"
import { useTranslate } from "@tolgee/react"
import { useState } from "react"

type MenuLink = { href: string; label: string; badge?: number }

export const BarebonesUserDropdown = ({
  user,
}: {
  user: HttpTypes.StoreCustomer | null
}) => {
  const [open, setOpen] = useState(false)
  const { unreadMessageCount: unreads } = useChat()
  const { t } = useTranslate()

  const accountLinks: MenuLink[] = [
    { href: "/user/orders", label: t("user_dropdown.orders") },
    {
      href: "/user/messages",
      label: t("user_dropdown.messages"),
      badge: unreads || undefined,
    },
    { href: "/user/returns", label: t("user_dropdown.returns") },
    { href: "/user/addresses", label: t("user_dropdown.addresses") },
    { href: "/user/reviews", label: t("user_dropdown.reviews") },
    { href: "/user/wishlist", label: t("user_dropdown.wishlist") },
    { href: "/user/affiliatex", label: t("user_dropdown.affiliatex") },
  ]

  const guestLinks: MenuLink[] = [
    { href: "/user", label: t("user_dropdown.login") },
    { href: "/user/register", label: t("user_dropdown.register") },
  ]

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <LocalizedClientLink
        href="/user"
        className="bb-nav-trigger relative inline-flex items-center justify-center text-[#2b2118] transition-opacity hover:opacity-70"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <ProfileIcon size={20} />
      </LocalizedClientLink>

      <div
        className={`bb-dropdown bb-dropdown--end absolute right-0 top-full z-50 pt-3 ${
          open ? "bb-dropdown--open" : ""
        }`}
        aria-hidden={!open}
      >
        <div role="menu" className="bb-dropdown-panel min-w-[220px]">
          {user ? (
            <>
              <p className="bb-dropdown-heading">
                {t("user_dropdown.account_heading")}
              </p>
              {accountLinks.map((link) => (
                <LocalizedClientLink
                  key={link.href}
                  href={link.href}
                  role="menuitem"
                  className="bb-dropdown-item relative"
                >
                  {link.label}
                  {Boolean(link.badge) && (
                    <Badge className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 p-0">
                      {link.badge}
                    </Badge>
                  )}
                </LocalizedClientLink>
              ))}
              <div className="bb-dropdown-divider" />
              <LocalizedClientLink
                href="/user/settings"
                role="menuitem"
                className="bb-dropdown-item"
              >
                {t("user_dropdown.settings")}
              </LocalizedClientLink>
              <LogoutButton
                unstyled
                className="bb-dropdown-item bb-dropdown-item--danger w-full text-left"
              />
            </>
          ) : (
            guestLinks.map((link) => (
              <LocalizedClientLink
                key={link.href}
                href={link.href}
                role="menuitem"
                className="bb-dropdown-item"
              >
                {link.label}
              </LocalizedClientLink>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
